/**
 * Planes por etapas (Req. 21; design.md, 8.8 y 8.9).
 *
 * Todo cambio de plan va en una sola transacción de `plans` y `stages`: si
 * algo falla, nada cambia. Las etapas pasadas nunca se reescriben; editar
 * metas parte la etapa en curso, y terminar un plan solo quita lo que todavía
 * no empieza (Req. 21.12, 21.13).
 */

import { db } from './schema';
import { enEscritura } from './tx';
import { nuevoId } from './ids';
import type { EtapaPlantilla, Plan, ReemplazoDePlan, Stage } from './types';
import {
  ahoraIso,
  esFechaLocal,
  formatearCortoConAnio,
  sumarDias,
  type FechaLocal,
} from '@/domain/dates';
import { fechaLocalDe } from '@/domain/time';
import {
  NOMBRE_ETAPA,
  cambiarMetasDesde,
  primerTraslape,
  programar,
  recorrerAlCambiarFin,
  siguientesCiclos,
  terminarEl,
  validarPlantilla,
  type EtapaProgramada,
  type MetasEtapa,
} from '@/domain/etapas';
import { ErrorValidacion } from '@/domain/errors';

export interface DatosDePlanes {
  planes: Plan[];
  etapas: Stage[];
}

/** Cuántos planes hay, activos o terminados. LECTURA PURA. */
export async function contarPlanes(): Promise<number> {
  return db.plans.count();
}

/** Todos los planes y etapas (son pocos). LECTURA PURA. */
export async function leerPlanes(): Promise<DatosDePlanes> {
  const [planes, etapas] = await Promise.all([db.plans.toArray(), db.stages.toArray()]);
  planes.sort((a, b) => b.fechaInicio.localeCompare(a.fechaInicio));
  etapas.sort((a, b) => a.fechaInicio.localeCompare(b.fechaInicio));
  return { planes, etapas };
}

export function planActivo(planes: readonly Plan[]): Plan | null {
  return planes.find((p) => p.estado === 'activo') ?? null;
}

export interface PlanNuevo {
  /** Creado al abrir el formulario: tocar dos veces "Crear" no duplica el plan. */
  id: string;
  nombre: string;
  fechaInicio: FechaLocal;
  repetir: boolean;
  plantilla: EtapaPlantilla[];
  notas: string | null;
}

function validarPlanNuevo(nuevo: PlanNuevo): void {
  if (nuevo.nombre.trim() === '') throw new ErrorValidacion('Escribe el nombre del plan.', 'nombre');
  if (!esFechaLocal(nuevo.fechaInicio)) throw new ErrorValidacion('La fecha de inicio no es válida.', 'fechaInicio');
  const error = validarPlantilla(nuevo.plantilla, nuevo.repetir);
  if (error !== null) throw new ErrorValidacion(error);
  // La meta de kcal del déficit y del superávit se define al crear el plan (Req. 21.3)
  for (const e of nuevo.plantilla) {
    if (e.tipo !== 'mantenimiento' && e.metaKcal === null) {
      throw new ErrorValidacion(`Falta la meta de kcal de la etapa de ${NOMBRE_ETAPA[e.tipo].toLowerCase()}.`);
    }
  }
}

function conIds(planId: string, programadas: readonly EtapaProgramada[], ahora: string): Stage[] {
  return programadas.map((e) => ({ ...e, id: nuevoId('eta'), planId, creadoEn: ahora, actualizadoEn: ahora }));
}

/** Por qué no se puede: la etapa de otro plan con la que coincidiría. */
export function mensajeTraslape(plan: Plan | undefined, etapa: Stage): string {
  const nombre = plan === undefined ? 'otro plan' : `el plan "${plan.nombre}"`;
  const fin = etapa.fechaFin === null ? 'sin fin' : `al ${formatearCortoConAnio(etapa.fechaFin)}`;
  return (
    `Se traslapa con ${nombre} (${NOMBRE_ETAPA[etapa.tipo].toLowerCase()} del ` +
    `${formatearCortoConAnio(etapa.fechaInicio)} ${fin}). Las etapas de planes distintos no pueden coincidir.`
  );
}

export interface RevisionPlanNuevo {
  etapas: EtapaProgramada[];
  /** El plan activo que se terminaría el día anterior al inicio (Req. 21.5). */
  terminaActivo: { plan: Plan; ultimoDia: FechaLocal } | null;
  /** Si no se puede crear, por qué. */
  problema: string | null;
}

async function revisar(
  nuevo: PlanNuevo,
  planes: Plan[],
  etapas: Stage[],
  hoy: FechaLocal,
): Promise<RevisionPlanNuevo> {
  validarPlanNuevo(nuevo);
  const programadas = programar(nuevo.plantilla, nuevo.repetir, nuevo.fechaInicio);
  const activo = planes.find((p) => p.estado === 'activo' && p.id !== nuevo.id) ?? null;
  let terminaActivo: RevisionPlanNuevo['terminaActivo'] = null;
  let restantes = etapas.filter((e) => e.planId !== nuevo.id);

  if (activo !== null) {
    // Terminar el activo en una fecha pasada borraría metas de días que ya
    // ocurrieron (Req. 21.13): con un plan activo, el nuevo empieza hoy o después.
    if (nuevo.fechaInicio < hoy) {
      return {
        etapas: programadas,
        terminaActivo: null,
        problema:
          `Tienes activo "${activo.nombre}": el plan nuevo debe empezar hoy o después, ` +
          'para que los días que ya pasaron conserven sus metas.',
      };
    }
    const ultimoDia = sumarDias(nuevo.fechaInicio, -1);
    if (activo.fechaInicio > ultimoDia) {
      return {
        etapas: programadas,
        terminaActivo: null,
        problema:
          `Tu plan "${activo.nombre}" empieza el ${formatearCortoConAnio(activo.fechaInicio)}, ` +
          'el mismo día o después del nuevo. Bórralo o termínalo antes de crear este.',
      };
    }
    terminaActivo = { plan: activo, ultimoDia };
    const corte = terminarEl(restantes.filter((e) => e.planId === activo.id), ultimoDia);
    restantes = restantes
      .filter((e) => !corte.borrar.includes(e.id))
      .map((e) => {
        const recorte = corte.recortar.find((r) => r.id === e.id);
        return recorte === undefined ? e : { ...e, fechaFin: recorte.fechaFin };
      });
  }

  const traslape = primerTraslape(programadas, restantes);
  return {
    etapas: programadas,
    terminaActivo,
    problema: traslape === null ? null : mensajeTraslape(planes.find((p) => p.id === traslape.planId), traslape),
  };
}

/** Lo que pasaría al crear el plan, para mostrarlo antes de confirmar. */
export async function revisarPlanNuevo(nuevo: PlanNuevo, hoy: FechaLocal): Promise<RevisionPlanNuevo> {
  const { planes, etapas } = await leerPlanes();
  return revisar(nuevo, planes, etapas, hoy);
}

/**
 * Crea el plan con sus etapas programadas. Si hay un plan activo, lo termina
 * el día anterior al inicio, solo si `terminarActivo` lo confirma, y guarda
 * en el plan nuevo lo que le cambió para poder deshacerlo (`borrarPlan`).
 */
export async function crearPlan(
  nuevo: PlanNuevo,
  opciones: { terminarActivo: boolean; hoy: FechaLocal },
): Promise<Plan> {
  return enEscritura([db.plans, db.stages], async () => {
    const existente = await db.plans.get(nuevo.id);
    if (existente !== undefined) return existente; // segundo toque de "Crear"

    const planes = await db.plans.toArray();
    const etapas = await db.stages.toArray();
    const revision = await revisar(nuevo, planes, etapas, opciones.hoy);
    if (revision.problema !== null) throw new ErrorValidacion(revision.problema);
    const ahora = ahoraIso();

    let reemplazo: ReemplazoDePlan | null = null;
    if (revision.terminaActivo !== null) {
      if (!opciones.terminarActivo) {
        throw new ErrorValidacion(`Confirma que quieres terminar el plan "${revision.terminaActivo.plan.nombre}".`);
      }
      reemplazo = await aplicarTermino(revision.terminaActivo.plan, revision.terminaActivo.ultimoDia, ahora);
    }

    const plan: Plan = {
      id: nuevo.id,
      nombre: nuevo.nombre.trim(),
      fechaInicio: nuevo.fechaInicio,
      fechaFin: null,
      repetir: nuevo.repetir,
      plantilla: nuevo.plantilla.map((e) => ({ ...e })),
      estado: 'activo',
      notas: nuevo.notas?.trim() || null,
      reemplazo,
      creadoEn: ahora,
      actualizadoEn: ahora,
    };
    await db.plans.put(plan);
    await db.stages.bulkPut(conIds(plan.id, revision.etapas, ahora));
    return plan;
  });
}

/** Termina un plan y devuelve lo que le cambió, para poder deshacerlo. */
async function aplicarTermino(plan: Plan, ultimoDia: FechaLocal, ahora: string): Promise<ReemplazoDePlan> {
  const etapas = await db.stages.where('planId').equals(plan.id).toArray();
  const corte = terminarEl(etapas, ultimoDia);
  const reemplazo: ReemplazoDePlan = {
    planId: plan.id,
    estadoAnterior: plan.estado,
    fechaFinAnterior: plan.fechaFin,
    etapasBorradas: etapas.filter((e) => corte.borrar.includes(e.id)),
    recortes: corte.recortar.map((r) => ({
      id: r.id,
      fechaFinAnterior: etapas.find((e) => e.id === r.id)?.fechaFin ?? null,
    })),
  };
  await db.stages.bulkDelete(corte.borrar);
  for (const r of corte.recortar) await db.stages.update(r.id, { fechaFin: r.fechaFin, actualizadoEn: ahora });
  await db.plans.put({ ...plan, estado: 'terminado', fechaFin: ultimoDia, actualizadoEn: ahora });
  return reemplazo;
}

/**
 * Termina el plan hoy o ayer, sin borrar lo que ya pasó (Req. 21.13). Un día
 * anterior borraría metas de días ya vividos, y uno futuro dejaría el plan
 * terminado pero todavía marcando las metas. Para alargar o acortar una
 * etapa está cambiarFinDeEtapa.
 */
export async function terminarPlan(planId: string, ultimoDia: FechaLocal, hoy: FechaLocal): Promise<void> {
  if (!esFechaLocal(ultimoDia)) throw new ErrorValidacion('La fecha no es válida.');
  if (ultimoDia !== hoy && ultimoDia !== sumarDias(hoy, -1)) {
    throw new ErrorValidacion('El último día del plan solo puede ser hoy o ayer: lo que ya pasó no se borra.');
  }
  await enEscritura([db.plans, db.stages], async () => {
    const plan = await db.plans.get(planId);
    if (plan === undefined) throw new ErrorValidacion('Ese plan ya no existe.');
    if (plan.estado !== 'activo') throw new ErrorValidacion('Ese plan ya estaba terminado.');
    if (ultimoDia < plan.fechaInicio) {
      throw new ErrorValidacion('El plan todavía no empieza ese día: bórralo en lugar de terminarlo.');
    }
    await aplicarTermino(plan, ultimoDia, ahoraIso());
  });
}

/**
 * Solo se borra un plan sin historial: creado hoy por error o que todavía no
 * empieza. Uno con días pasados se termina (design.md, 7.9).
 */
export function sePuedeBorrar(plan: Pick<Plan, 'creadoEn' | 'fechaInicio'>, hoy: FechaLocal): boolean {
  let creadoHoy = false;
  try {
    creadoHoy = fechaLocalDe(plan.creadoEn) === hoy;
  } catch {
    creadoHoy = false;
  }
  return creadoHoy || plan.fechaInicio > hoy;
}

/**
 * Borra un plan creado por error. Si al crearlo terminó otro plan, ese vuelve
 * exactamente como estaba, con sus etapas, en la misma transacción.
 */
export async function borrarPlan(planId: string, hoy: FechaLocal): Promise<void> {
  await enEscritura([db.plans, db.stages], async () => {
    const plan = await db.plans.get(planId);
    if (plan === undefined) return;
    if (!sePuedeBorrar(plan, hoy)) {
      throw new ErrorValidacion('Este plan ya tiene días en tu historial: termínalo en lugar de borrarlo.');
    }
    await db.stages.where('planId').equals(planId).delete();
    await db.plans.delete(planId);

    const reemplazo = plan.reemplazo ?? null;
    if (reemplazo === null) return;
    const anterior = await db.plans.get(reemplazo.planId);
    if (anterior === undefined) return;
    const ahora = ahoraIso();
    await db.plans.put({
      ...anterior,
      estado: reemplazo.estadoAnterior,
      fechaFin: reemplazo.fechaFinAnterior,
      actualizadoEn: ahora,
    });
    for (const r of reemplazo.recortes) {
      await db.stages.update(r.id, { fechaFin: r.fechaFinAnterior, actualizadoEn: ahora });
    }
    await db.stages.bulkPut(reemplazo.etapasBorradas);
  });
}

/** Metas nuevas para un tipo de etapa desde un día, partiendo la etapa en curso (Req. 21.12). */
export async function cambiarMetas(
  planId: string,
  indice: number,
  desde: FechaLocal,
  metas: MetasEtapa,
  hoy: FechaLocal,
): Promise<void> {
  if (!esFechaLocal(desde)) throw new ErrorValidacion('La fecha no es válida.');
  if (desde < hoy) {
    throw new ErrorValidacion('Las metas nuevas aplican desde hoy o después: los días pasados conservan las suyas.');
  }
  await enEscritura([db.plans, db.stages], async () => {
    const plan = await db.plans.get(planId);
    if (plan === undefined) throw new ErrorValidacion('Ese plan ya no existe.');
    if (plan.estado !== 'activo') throw new ErrorValidacion('Ese plan ya terminó.');
    const etapas = await db.stages.where('planId').equals(planId).toArray();
    let cambio;
    try {
      cambio = cambiarMetasDesde(plan, etapas, indice, desde, metas);
    } catch (error) {
      throw new ErrorValidacion(error instanceof Error ? error.message : 'Las metas no son válidas.');
    }
    const ahora = ahoraIso();
    for (const { id, cambios } of cambio.actualizar) {
      await db.stages.update(id, { ...cambios, actualizadoEn: ahora });
    }
    await db.stages.bulkPut(conIds(planId, cambio.crear, ahora));
    await db.plans.put({ ...plan, plantilla: cambio.plantilla, actualizadoEn: ahora });
  });
}

/**
 * Cambia el último día de la etapa actual o de una futura y recorre las
 * siguientes del plan los mismos días (Req. 21.9; design.md, 8.8), en una
 * sola transacción. Antes de guardar revisa que nada se traslape con etapas
 * de otros planes (Req. 21.11). Devuelve los días que se recorrieron.
 */
export async function cambiarFinDeEtapa(
  planId: string,
  etapa: { ciclo: number; indice: number },
  nuevoFin: FechaLocal,
  hoy: FechaLocal,
): Promise<number> {
  return enEscritura([db.plans, db.stages], async () => {
    const plan = await db.plans.get(planId);
    if (plan === undefined) throw new ErrorValidacion('Ese plan ya no existe.');
    if (plan.estado !== 'activo') throw new ErrorValidacion('Ese plan ya terminó.');
    const todas = await db.stages.toArray();
    let cambio;
    try {
      cambio = recorrerAlCambiarFin(todas.filter((e) => e.planId === planId), etapa, nuevoFin, hoy);
    } catch (error) {
      throw new ErrorValidacion(error instanceof Error ? error.message : 'No se pudo cambiar el fin de la etapa.');
    }
    if (cambio.dias === 0) return 0;
    const traslape = primerTraslape(cambio.etapas, todas.filter((e) => e.planId !== planId));
    if (traslape !== null) {
      throw new ErrorValidacion(mensajeTraslape(await db.plans.get(traslape.planId), traslape));
    }
    const ahora = ahoraIso();
    await db.stages.bulkDelete(cambio.borrar);
    for (const { id, cambios } of cambio.actualizar) {
      await db.stages.update(id, { ...cambios, actualizadoEn: ahora });
    }
    await db.plans.update(planId, { actualizadoEn: ahora });
    return cambio.dias;
  });
}

/** Programa otro año de ciclos después de lo programado (Req. 21.4). */
export async function agregarCiclos(planId: string): Promise<number> {
  return enEscritura([db.plans, db.stages], async () => {
    const plan = await db.plans.get(planId);
    if (plan === undefined || plan.estado !== 'activo' || !plan.repetir) {
      throw new ErrorValidacion('Solo se agregan ciclos a un plan activo que se repite.');
    }
    const etapas = await db.stages.toArray();
    const delPlan = etapas.filter((e) => e.planId === planId);
    const nuevas = siguientesCiclos(plan, delPlan);
    const traslape = primerTraslape(nuevas, etapas.filter((e) => e.planId !== planId));
    if (traslape !== null) {
      throw new ErrorValidacion(mensajeTraslape(await db.plans.get(traslape.planId), traslape));
    }
    const ahora = ahoraIso();
    await db.stages.bulkPut(conIds(planId, nuevas, ahora));
    await db.plans.update(planId, { actualizadoEn: ahora });
    return new Set(nuevas.map((e) => e.ciclo)).size;
  });
}
